// src/pages/custom/LedgerFinal.js
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import './LedgerFinal.css';

const API_URL = `${process.env.REACT_APP_API_PATH}`;

function LedgerFinal() {
  const [loanAccountNo, setLoanAccountNo] = useState('');
  const [ledgerData, setLedgerData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);
  const reportRef = useRef(null);

  const handleChange = (e) => {
    setLoanAccountNo(e.target.value);
  };

  const fetchLedger = async () => {
    if (!loanAccountNo.trim()) {
      setError('Please enter a Loan Account Number');
      return;
    }

    setLoading(true);
    setError(null);
    setDebugInfo(null);
    
    try {
      console.log('Fetching ledger for:', loanAccountNo.trim());
      const res = await axios.get(`${API_URL}LoanLedger/${loanAccountNo.trim()}`);
      
      console.log('API Response Status:', res.status);
      console.log('API Response Data:', res.data);
      
      if (res.data) {
        // Debug: Check if EMI schedule exists
        console.log('Loan Details:', res.data.loanDetails);
        console.log('EMI Schedule:', res.data.emiSchedule);
        console.log('EMI Schedule Length:', res.data.emiSchedule?.length || 0);
        console.log('Payment Summary:', res.data.paymentSummary);
        
        // Check if EMI schedule is empty
        if (!res.data.emiSchedule || res.data.emiSchedule.length === 0) {
          setDebugInfo('No EMI records found in the response. Please check the stored procedure.');
        } else {
          setDebugInfo(`Found ${res.data.emiSchedule.length} EMI records`);
        }
        
        setLedgerData(res.data);
      } else {
        setError('Failed to fetch ledger data');
      }
    } catch (err) {
      console.error('Error fetching ledger:', err);
      console.error('Error response:', err.response);
      console.error('Error message:', err.message);
      
      if (err.response?.status === 404) {
        setError('Loan account not found. Please check the account number.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please check the stored procedure.');
      } else {
        setError(err.response?.data?.error || err.message || 'Server error. Please try again.');
      }
      setLedgerData(null);
    }
    setLoading(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchLedger();
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '₹0';
    return `₹${Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const getStatusBadge = (status) => {
    if (!status) return '⏳ Pending';
    if (status === 'Paid') return '✅ Paid';
    if (status === 'Unpaid') return '❌ Unpaid';
    if (status === 'Partial') return '⚠️ Partial';
    return '⏳ Pending';
  };

  const getStatusColor = (status) => {
    if (!status) return '#666';
    if (status === 'Paid') return '#2e7d32';
    if (status === 'Unpaid') return '#d32f2f';
    if (status === 'Partial') return '#ed6c02';
    return '#666';
  };

  // Debug component to show what's in the data
  const renderDebugInfo = () => {
    if (!debugInfo) return null;
    return (
      <div className="mfl-debug">
        <strong>Debug Info:</strong> {debugInfo}
        {ledgerData?.emiSchedule && (
          <div style={{ marginTop: '5px', fontSize: '12px' }}>
            {/* <div>EMI Schedule Type: {Array.isArray(ledgerData.emiSchedule) ? 'Array' : typeof ledgerData.emiSchedule}</div>
            <div>EMI Count: {ledgerData.emiSchedule.length}</div>
            <div>First EMI: {ledgerData.emiSchedule.length > 0 ? JSON.stringify(ledgerData.emiSchedule[0]) : 'None'}</div> */}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mfl-app">
      <header className="mfl-header">
        <h1>🏦 Loan Ledger Report</h1>
        <p>Complete loan repayment history and summary</p>
      </header>

      <div className="mfl-container">
        <div className="mfl-form-card">
          <div className="mfl-form-grid">
            <div className="mfl-form-group">
              <label>Loan Account Number</label>
              <input 
                type="text" 
                value={loanAccountNo} 
                onChange={handleChange}
                onKeyPress={handleKeyPress}
                placeholder="Enter Loan Account Number (e.g., P000344-02)"
                disabled={loading}
              />
            </div>
            <div className="mfl-form-group" style={{ justifyContent: 'flex-end' }}>
              <button 
                className="mfl-btn mfl-btn-primary" 
                onClick={fetchLedger} 
                disabled={loading}
                style={{ height: '42px' }}
              >
                {loading ? '⏳ Loading...' : '🔍 View Ledger'}
              </button>
            </div>
          </div>
          <div className="mfl-btn-group">
            {ledgerData && (
              <button className="mfl-btn mfl-btn-print" onClick={handlePrint}>
                🖨️ Print / PDF
              </button>
            )}
          </div>
          {error && <div className="mfl-error">{error}</div>}
          {renderDebugInfo()}
        </div>

        {ledgerData && !loading && (
          <div className="mfl-report" ref={reportRef} id="report">
            
            <div className="mfl-report-header">
              <div>
                <h2>LOAN LEDGER REPORT</h2>
                <div className="mfl-sub">Complete Loan Repayment History</div>
              </div>
              <div className="mfl-meta">
                <div><strong>Loan Account:</strong> {ledgerData.loanDetails?.loanAccountNo}</div>
                <div><strong>Generated:</strong> {new Date().toLocaleString()}</div>
              </div>
            </div>

            {/* Loan Details */}
            <div className="mfl-section">
              <h3>LOAN DETAILS</h3>
              <div className="mfl-loan-summary">
                <div><strong>Member Name:</strong> {ledgerData.loanDetails?.memberName}</div>
                <div><strong>Group ID:</strong> {ledgerData.loanDetails?.groupID}</div>
                <div><strong>Member ID:</strong> {ledgerData.loanDetails?.memberId}</div>
                <div><strong>Branch:</strong> {ledgerData.loanDetails?.branchCode}</div>
                <div><strong>Status:</strong> 
                  {ledgerData.loanDetails?.loanStatus === 'Active' ? '✅ Active' : '🔒 Closed'}
                </div>
                <div><strong>Loan Amount:</strong> {formatCurrency(ledgerData.loanDetails?.originalLoanAmount)}</div>
                <div><strong>Disbursed:</strong> {formatCurrency(ledgerData.loanDetails?.disbursedAmount)}</div>
                <div><strong>EMI Amount:</strong> {formatCurrency(ledgerData.loanDetails?.emiAmount)}</div>
                <div><strong>Interest Rate:</strong> {ledgerData.loanDetails?.annualInterestRate}%</div>
                <div><strong>Total EMIs:</strong> {ledgerData.loanDetails?.totalEMIs}</div>
                <div><strong>Payment Frequency:</strong> {ledgerData.loanDetails?.loanPaymentFrequency}</div>
                <div><strong>Disbursement Date:</strong> {ledgerData.loanDetails?.disbursementDate}</div>
                <div><strong>Contract Start:</strong> {ledgerData.loanDetails?.contractStartDate}</div>
                <div><strong>Contract End:</strong> {ledgerData.loanDetails?.contractEndDate}</div>
              </div>
              <div className="mfl-address">
                <strong>Address:</strong> {ledgerData.loanDetails?.addressLine1} {ledgerData.loanDetails?.addressLine2}
              </div>
            </div>

            {/* EMI SCHEDULE - FIXED */}
            <div className="mfl-section">
              <h3>EMI SCHEDULE</h3>
              {ledgerData.emiSchedule && ledgerData.emiSchedule.length > 0 ? (
                <>
                  <div className="mfl-table-wrap">
                    <table className="mfl-table">
                      <thead>
                        <tr>
                          <th>EMI No</th>
                          <th>Date</th>
                          <th>Opening Balance</th>
                          <th>Principal</th>
                          <th>Interest</th>
                          <th>Scheduled</th>
                          <th>Received</th>
                          <th>Closing Balance</th>
                          <th>Pay Mode</th>
                          <th>Status</th>
                          <th>Overdue</th>
                          <th>Alerted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ledgerData.emiSchedule.map((item, index) => {
                          // Ensure we have data even if some fields are missing
                          const emiNo = item.emiNo || item.EMINo || index + 1;
                          const emiDate = item.emiDate || item.EMIDate || '';
                          const openingBalance = item.openingBalance || item.OpeningBalance || 0;
                          const principal = item.principalComponent || item.PrincipalComponent || 0;
                          const interest = item.interestComponent || item.InterestComponent || 0;
                          const scheduled = item.scheduledAmount || item.ScheduledAmount || 0;
                          const received = item.totalReceived || item.TotalReceived || 0;
                          const closingBalance = item.closingBalance || item.ClosingBalance || 0;
                          const payMode = item.payMode || item.PAYMode || 'N/A';
                          const paymentStatus = item.paymentStatus || item.PaymentStatus || 'Pending';
                          const overdue = item.overdue || item.Overdue || '';
                          const alerted = item.alerted || item.Alerted || '';
                          
                          return (
                            <tr key={index} className={paymentStatus === 'Unpaid' || paymentStatus === 'Partial' ? 'mfl-overdue-row' : ''}>
                              <td><strong>{emiNo}</strong></td>
                              <td>{emiDate}</td>
                              <td>{formatCurrency(openingBalance)}</td>
                              <td>{formatCurrency(principal)}</td>
                              <td>{formatCurrency(interest)}</td>
                              <td>{formatCurrency(scheduled)}</td>
                              <td>
                                {received > 0 ? (
                                  <span style={{ color: '#2e7d32', fontWeight: 'bold' }}>
                                    {formatCurrency(received)}
                                  </span>
                                ) : (
                                  <span style={{ color: '#999' }}>₹0.00</span>
                                )}
                              </td>
                              <td>{formatCurrency(closingBalance)}</td>
                              <td>{payMode}</td>
                              <td>
                                <span style={{ 
                                  color: getStatusColor(paymentStatus),
                                  fontWeight: 'bold'
                                }}>
                                  {getStatusBadge(paymentStatus)}
                                </span>
                              </td>
                              <td>{overdue === '✓' ? '⚠️ Yes' : ''}</td>
                              <td>{alerted === '✓' ? '🔔 Yes' : ''}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="mfl-total">
                          <td colSpan="5"><strong>TOTALS</strong></td>
                          <td><strong>{formatCurrency(ledgerData.paymentSummary?.totalScheduledAmount || ledgerData.paymentSummary?.TotalScheduledAmount)}</strong></td>
                          <td><strong>{formatCurrency(ledgerData.paymentSummary?.totalReceivedAmount || ledgerData.paymentSummary?.TotalReceivedAmount)}</strong></td>
                          <td colSpan="5"></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  <div className="mfl-table-note">
                    <span>✅ Paid &nbsp;|&nbsp; ❌ Unpaid &nbsp;|&nbsp; ⚠️ Partial &nbsp;|&nbsp; ⏳ Pending</span>
                    <span style={{ marginLeft: '20px' }}>Total EMIs: <strong>{ledgerData.emiSchedule.length}</strong></span>
                  </div>
                </>
              ) : (
                <div className="mfl-no-data">
                  <p>No EMI records found for this loan account.</p>
                  {debugInfo && <p style={{ fontSize: '12px', color: '#666' }}>{debugInfo}</p>}
                </div>
              )}
            </div>

            {/* Payment Summary */}
            <div className="mfl-section">
              <h3>PAYMENT SUMMARY</h3>
              <div className="mfl-summary-grid">
                <div className="mfl-summary-card">
                  <div className="mfl-summary-value">{ledgerData.paymentSummary?.emIsPaid || ledgerData.paymentSummary?.EMIsPaid || 0}</div>
                  <div className="mfl-summary-label">EMIs Paid</div>
                </div>
                <div className="mfl-summary-card">
                  <div className="mfl-summary-value">{formatCurrency(ledgerData.paymentSummary?.totalAmountPaid || ledgerData.paymentSummary?.TotalAmountPaid)}</div>
                  <div className="mfl-summary-label">Total Paid</div>
                </div>
                <div className="mfl-summary-card mfl-highlight-orange">
                  <div className="mfl-summary-value">{formatCurrency(ledgerData.paymentSummary?.totalOverdue || ledgerData.paymentSummary?.TotalOverdue)}</div>
                  <div className="mfl-summary-label">Total Overdue</div>
                </div>
                <div className="mfl-summary-card mfl-highlight-red">
                  <div className="mfl-summary-value">{formatCurrency(ledgerData.paymentSummary?.outstanding || ledgerData.paymentSummary?.Outstanding)}</div>
                  <div className="mfl-summary-label">Outstanding</div>
                </div>
                <div className="mfl-summary-card mfl-highlight-purple">
                  <div className="mfl-summary-value">{formatCurrency(ledgerData.paymentSummary?.foreclose || ledgerData.paymentSummary?.Foreclose)}</div>
                  <div className="mfl-summary-label">Foreclose Amount</div>
                </div>
                <div className="mfl-summary-card">
                  <div className="mfl-summary-value">{ledgerData.paymentSummary?.totalTransactions || ledgerData.paymentSummary?.TotalTransactions || 0}</div>
                  <div className="mfl-summary-label">Total Transactions</div>
                </div>
              </div>

              <div className="mfl-summary-details">
                <div><strong>Total Principal Paid:</strong> {formatCurrency(ledgerData.paymentSummary?.totalPrincipalPaid || ledgerData.paymentSummary?.TotalPrincipalPaid)}</div>
                <div><strong>Total Interest Paid:</strong> {formatCurrency(ledgerData.paymentSummary?.totalInterestPaid || ledgerData.paymentSummary?.TotalInterestPaid)}</div>
                <div><strong>Total Scheduled Amount:</strong> {formatCurrency(ledgerData.paymentSummary?.totalScheduledAmount || ledgerData.paymentSummary?.TotalScheduledAmount)}</div>
                <div><strong>Total Received Amount:</strong> {formatCurrency(ledgerData.paymentSummary?.totalReceivedAmount || ledgerData.paymentSummary?.TotalReceivedAmount)}</div>
                <div><strong>First Payment Date:</strong> {ledgerData.paymentSummary?.firstPaymentDate || ledgerData.paymentSummary?.FirstPaymentDate ? new Date(ledgerData.paymentSummary.firstPaymentDate || ledgerData.paymentSummary.FirstPaymentDate).toLocaleDateString() : 'N/A'}</div>
                <div><strong>Last Payment Date:</strong> {ledgerData.paymentSummary?.lastPaymentDate || ledgerData.paymentSummary?.LastPaymentDate ? new Date(ledgerData.paymentSummary.lastPaymentDate || ledgerData.paymentSummary.LastPaymentDate).toLocaleDateString() : 'N/A'}</div>
              </div>
            </div>

            <div className="mfl-footer">
              Generated on {new Date().toLocaleString()} &bull; Loan Ledger Report &bull; {ledgerData.loanDetails?.loanAccountNo}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default LedgerFinal;